<template>
  <div class="form-group mb-3">
    <label v-if="label" :class="{ 'text-danger': hasError }">
      {{ label }}
    </label>
    <div class="input-group">
      <input
        :type="isFocused || !value ? 'date' : 'text'"
        :class="[inputClass, 'form-control custom-input shadow_inset_sm', { 'is-invalid': hasError }]"
        :value="isFocused ? value : formattedValue"
        :placeholder="placeholder"
        :min="minDateStr"
        :max="maxDateStr"
        @focus="isFocused = true"
        @blur="isFocused = false"
        @input="onInputNative"
        @keyup.enter="onEnter"
      />
      <div v-if="(clearable && value) || $slots.append" class="input-group-append">
        <ButtonProvider
          v-if="clearable && value"
          variant="dark"
          icon="fa-times"
          @click="onClear"
        />
        <slot name="append"></slot>
      </div>
    </div>
    <small v-if="hasError" class="form-text text-danger p-0 m-0">
      {{ errorMessage }}
    </small>
  </div>
</template>

<script>

/**
 * /--------------------------/
 * COMPONENTE: Datepicker 
 * /--------------------------/
 * 
 * Este Componente es un DatePicker que permite varias distintas acciones, por ejemplo:
 * - Desactivar fechas
 * - Solo permitir fechas del mes actual
 * - Solo permitir fechas en un rango
 * - Solo permitir fechas futuras o pasadas
 * - Solo permitir fechas posteriores a una fecha específica
 * - Solo permitir fechas anteriores a una fecha específica
 * 
 * --- INSTRUCCIONES DE USO POR PROP ---
 * 
 * @prop {String} inputClass
 *       Uso: `:inputClass="'custom-class'"`
 *       Efecto: Añade clases de estilos al input.
 * 
 * @prop {Boolean|String} onlyCurrentMonth
 *       Uso: `:only-current-month="true"`
 *       Importante: No usar junto con | rangeDates |
 *       Efecto: Bloquea automáticamente todos los días que no pertenezcan al mes en curso.
 * 
 * @prop {Object} rangeDates 
 *       Uso: `:rangeDates="{ start: '2026-05-01', end: '2026-05-15' }"`
 *       Importante: No usar junto con | onlyCurrentMonth |
 *       Efecto: Permite seleccionar únicamente fechas que se encuentren dentro de este rango (inclusive).
 * 
 * @prop {Date|String} fechaStart
 *       Uso: `:fecha-start="'2026-05-01'"`
 *       Efecto: Deshabilita todas las fechas anteriores a la indicada. Solo permite de esa fecha en adelante.
 * 
 * @prop {Date|String} fechaEnd
 *       Uso: `:fecha-end="'2026-05-31'"`
 *       Efecto: Deshabilita todas las fechas posteriores a la indicada. Solo permite hasta esa fecha.
 * 
 * @prop {Boolean} hasError / @prop {String} errorMessage
 *       Uso: Para mostrar errores de validación.
 *       Importante: Usar Ambos valores juntos para mostrar el error correctamente.
 *       Efecto: Muestra bordes rojos en el input y un mensaje de error debajo.
 * 
 * @prop {Boolean} clearable
 *       Uso: `:clearable="true"`
 *       Efecto: Muestra un botón para limpiar el valor del input.
 * 
 * @prop {Number} daysBeforeMonth
 *       Uso: `:days-before-month="2"`
 *       Efecto: Permite seleccionar una cantidad de días del mes anterior cuando onlyCurrentMonth está activo.
 * 
 * @prop {String} placeholder
 *       Uso: `:placeholder="'Seleccione fecha...'"`
 *       Efecto: Muestra un texto de ayuda cuando no hay fecha seleccionada.
 */

import { ButtonProvider } from '../buttons';

export default {
  name: 'InputDatePicker',
  components: {
    ButtonProvider
  },
  props: {
    label: { type: String, default: '' },
    value: { 
      type: [Date, String, Number], 
      default: null 
    },
    inputClass: { 
      type: [String, Object, Array], 
      default: '' 
    },
    placeholder: {
      type: String,
      default: ''
    },
    rangeDates: { 
      type: Object, 
      default: () => ({}) 
    },
    onlyCurrentMonth: { 
      type: [Boolean, String], 
      default: false 
    },
    fechaStart: { 
      type: [Date, String, Number], 
      default: null 
    },
    fechaEnd: { 
      type: [Date, String, Number], 
      default: null 
    },
    hasError: { 
      type: Boolean, 
      default: false 
    },
    errorMessage: { 
      type: String, 
      default: '' 
    },
    clearable: {
      type: Boolean,
      default: false
    },
    daysBeforeMonth: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isFocused: false
    };
  },
  mounted() {
    /**
     * Este bloque de código se encarga de validar que no se estén utilizando las props "onlyCurrentMonth" y "rangeDates" al mismo tiempo.
     * Si se encuentran ambas props activas, se mostrará una advertencia en la consola y seteara la prop "onlyCurrentMonth" a false.
     */
    if ((this.onlyCurrentMonth === true || this.onlyCurrentMonth === 'true') && 
        (this.rangeDates && (this.rangeDates.start || this.rangeDates.end))) {
      console.warn('[DatePicker] Advertencia: No se deben usar "onlyCurrentMonth" y "rangeDates" al mismo tiempo. Se desactivará "onlyCurrentMonth" en favor del rango especificado.');
      this.onlyCurrentMonth = false;
    }
  },
  computed: {

    /**
     * Método computado utilizado para calcular las fechas deshabilitadas basándose en las props.
     */
    computedDisabledDates() {
      let minTimes = [];
      let maxTimes = [];
      
      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth();
      
      // 1. Mes actual (Se ignora si hay un rangeDates activo, para evitar conflictos)
      const hasRangeDates = this.rangeDates && (this.rangeDates.start || this.rangeDates.end);
      if ((this.onlyCurrentMonth === true || this.onlyCurrentMonth === 'true') && !hasRangeDates) {
        // "to" desactiva fechas anteriores al límite establecido
        let minDate = new Date(y, m, 1);
        if (this.daysBeforeMonth > 0) {
          minDate.setDate(minDate.getDate() - this.daysBeforeMonth);
        }
        minTimes.push(minDate.getTime());
        
        // "from" desactiva fechas iguales y posteriores al 1 del siguiente mes
        maxTimes.push(new Date(y, m + 1, 1).getTime());
      }
      
      // 2. rangeDates
      if (this.rangeDates && this.rangeDates.start) {
        const d = this.parseDateLocal(this.rangeDates.start);
        if (d) minTimes.push(d.getTime());
      }
      if (this.rangeDates && this.rangeDates.end) {
        const d = this.parseDateLocal(this.rangeDates.end);
        if (d) {
          d.setDate(d.getDate() + 1);
          maxTimes.push(d.getTime());
        }
      }
      
      // 3. fechaStart/End
      if (this.fechaStart) {
        const d = this.parseDateLocal(this.fechaStart);
        if (d) minTimes.push(d.getTime());
      }
      if (this.fechaEnd) {
        const d = this.parseDateLocal(this.fechaEnd);
        if (d) {
          d.setDate(d.getDate() + 1);
          maxTimes.push(d.getTime());
        }
      }
      
      let computedDisabled = {};
      
      // El límite inferior más restrictivo es la fecha mayor de los límites "to"
      if (minTimes.length > 0) {
        const highestMin = Math.max.apply(null, minTimes);
        computedDisabled.to = new Date(highestMin);
      }
      
      // El límite superior más restrictivo es la fecha menor de los límites "from"
      if (maxTimes.length > 0) {
        const lowestMax = Math.min.apply(null, maxTimes);
        computedDisabled.from = new Date(lowestMax);
      }
      
      return computedDisabled;
    },

    /**
     * Devuelve la fecha mínima permitida en formato YYYY-MM-DD
     */
    minDateStr() {
      const disabled = this.computedDisabledDates;
      return disabled.to ? this.formatDateObj(disabled.to) : '';
    },

    /**
     * Devuelve la fecha máxima permitida en formato YYYY-MM-DD
     */
    maxDateStr() {
      const disabled = this.computedDisabledDates;
      if (disabled.from) {
        // El 'from' de vuejs-datepicker es exclusivo (deshabilita DESDE esa fecha).
        // Para el 'max' nativo, necesitamos el día inmediatamente anterior.
        const d = new Date(disabled.from);
        d.setDate(d.getDate() - 1);
        return this.formatDateObj(d);
      }
      return '';
    },

    /**
     * Devuelve la fecha formateada en DD/MM/YYYY para visualización
     */
    formattedValue() {
      if (!this.value) return '';
      // El valor ya viene en YYYY-MM-DD
      const parts = String(this.value).split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return this.value;
    }
  },
  methods: {

    /**
     * Método auxiliar utilizado para convertir fechas a un objeto Date local sin tener en cuenta la zona horaria
     * esto nos sirve para cuando recibimos fechas en formato string o number
     * y necesitamos compararlas con objetos Date
     */
    parseDateLocal(val) {
      if (!val) return null;
      if (val instanceof Date) {
        if (isNaN(val.getTime())) return null;
        return new Date(val.getFullYear(), val.getMonth(), val.getDate());
      }
      if (typeof val === 'string') {
        const dateStr = val.trim();
        if (dateStr.includes('-')) {
          const parts = dateStr.split('T')[0].split('-');
          if (parts.length === 3) {
            return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
          }
        }
        if (dateStr.includes('/')) {
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            return new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
          }
        }
        const d = new Date(dateStr);
        if (!isNaN(d.getTime())) {
          return new Date(d.getFullYear(), d.getMonth(), d.getDate());
        }
      }
      if (typeof val === 'number') {
        const d = new Date(val);
        if (!isNaN(d.getTime())) {
          return new Date(d.getFullYear(), d.getMonth(), d.getDate());
        }
      }
      return null;
    },

    /**
     * Método auxiliar utilizado para formatear fechas a un objeto Date local sin tener en cuenta la zona horaria
     */
    formatDateObj(d) {
      if (!d) return '';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    /**
     * Maneja el evento input del input nativo
     */
    onInputNative(event) {
      this.$emit('input', event.target.value);
    },

    /**
     * Maneja el evento enter
     */
    onEnter() {
      this.$emit('enter');
    },

    /**
     * Limpia el valor del input
     */
    onClear() {
      this.$emit('input', null);
    }
  },
  inheritAttrs: false
}
</script>

<style scoped>
  /**
  * Estilos para el input nativo
  */
  input[type="date"]:focus {
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  }

  input[type="date"].is-invalid:focus {
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
  }

  /* Las clases de Bootstrap (mb-3) se encargan del espaciado por defecto */
  
  .custom-input {
    border: 1px solid #89898995 !important;
    border-radius: 25px !important;
    padding: 12px 15px !important;
    height: auto !important;
    text-align: center !important;
    background-color: transparent !important;
    color: #45505e !important;
  }

  /* Ajustes para cuando hay un botón acoplado */
  .input-group > .custom-input:not(:last-child) {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }

  ::v-deep .input-group-append .btn {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    border-top-right-radius: 25px !important;
    border-bottom-right-radius: 25px !important;
    padding-left: 20px !important;
    padding-right: 20px !important;
    display: flex;
    align-items: center;
  }

  .custom-input::placeholder {
    color: #4d5968 !important;
    font-size: 13px !important;
    opacity: 1 !important;
  }
</style>